import { Schema } from "effect";
import { HttpApiEndpoint, HttpApiGroup } from "effect/unstable/httpapi";
import { StationId } from "../station-id.ts";
import { DeploymentBootstrapPolicy, IssuedBrowserStationSession, ValidatedPlatformIdentity } from "../development-authorization.ts";
import { STATION_HTTP_PREFIX, StationRevision } from "./http.ts";

export const FirstChangePayload = /* @__PURE__ */ (() => Schema.Struct({
  changeId: Schema.String, title: Schema.String, goal: Schema.String, policyDigest: Schema.String, initializationKey: Schema.String,
  expectedUpdatedAt: StationRevision, identity: Schema.Record(Schema.String, Schema.Json), bootstrapPolicy: Schema.Record(Schema.String, Schema.Json)
}))();
export const FirstChangeInput = /* @__PURE__ */ (() => Schema.Struct({
  ...FirstChangePayload.fields, stationId: StationId, identity: ValidatedPlatformIdentity, bootstrapPolicy: DeploymentBootstrapPolicy
}))();
export type FirstChangeInput = typeof FirstChangeInput.Type;
/** Local-owner RPC carries input, never the in-process identity capability.
 * Only the receiving coordinator's explicit development/test mode can mint it. */
export const FirstChangeLocalOwnerInput = /* @__PURE__ */ (() => Schema.Struct({
  ...FirstChangeInput.fields,
  identity: Schema.Struct({ subject: Schema.String, policySource: Schema.String, groups: Schema.Array(Schema.String) })
}))();
export type FirstChangeLocalOwnerInput = typeof FirstChangeLocalOwnerInput.Type;
export const FirstChangeSourceAuthority = /* @__PURE__ */ (() => Schema.Literals(["uninitialized", "legacy", "repository", "reversal-draining"]))();
// Decode the actor fields this boundary consumes. The actor owns the remaining
// lifecycle state and the immutable source/retention transaction.
export const FirstChangeMigrationResult = /* @__PURE__ */ (() => Schema.Struct({
  change: Schema.Struct({ changeId: Schema.String, lifecycleState: Schema.String }),
  mainCommitId: Schema.String, mainTreeId: Schema.String, mainRevision: StationRevision,
  migrationBackupRefName: Schema.String, stationUpdatedAt: StationRevision,
  repository: Schema.Struct({ sourceAuthority: FirstChangeSourceAuthority }),
  ownerGrant: Schema.Struct({ principalId: Schema.String })
}))();
export type FirstChangeMigrationResult = typeof FirstChangeMigrationResult.Type;
export const FirstChangeResponse = /* @__PURE__ */ (() => Schema.Struct({
  ok: Schema.Literal(true), stationId: StationId, ...FirstChangeMigrationResult.fields.change.fields,
  mainCommitId: Schema.String, mainTreeId: Schema.String, mainRevision: StationRevision,
  migrationBackupRefName: Schema.String, stationUpdatedAt: StationRevision, sourceAuthority: FirstChangeSourceAuthority,
  ownerPrincipalId: Schema.String, csrfToken: IssuedBrowserStationSession.fields.csrfToken, sessionExpiresAt: Schema.Number
}))();
export const FirstChangeApi = HttpApiGroup.make("firstChange").add(HttpApiEndpoint.post("initialize", `${STATION_HTTP_PREFIX}/:stationId/first-change`, {
  params: Schema.Struct({ stationId: Schema.String }), payload: FirstChangePayload, success: FirstChangeResponse,
  error: [400, 401, 403, 404, 409, 500].map(status => Schema.Struct({ error: Schema.String, code: Schema.optionalKey(Schema.String) }).annotate({ httpApiStatus: status }))
}));
